/**
 * Utilities for mapping column names to units and other properties in Erdos Miller files
 */

/**
 * Get the appropriate unit for a column based on its name and content
 * Handles all Erdos Miller file types: MicroPulse, Cortex, HopRx, HopTx
 * 
 * @param columnName The name of the column
 * @returns The unit string for the column
 */
export function getUnitForColumn(columnName: string): string {
  // Standard measurements
  if (columnName.includes('StDev') || columnName.includes('Standard Deviation')) return 'g RMS';
  if (columnName.includes('RPM') || columnName.includes('rpm')) return 'RPM';
  if (columnName.includes('ACCEL')) return 'G';
  if (columnName.includes('GAMMA')) return 'counts';
  if (columnName.includes('State')) return '';
  
  // Directional measurements
  if (columnName.includes('Inclination') || columnName.includes('INCL') || columnName.includes('INC')) return 'degrees';
  if (columnName.includes('Azimuth') || columnName.includes('AZIM') || columnName.includes('AZM')) return 'degrees';
  if (columnName.includes('Tool-Face') || columnName.includes('Dip Angle') || columnName.includes('GTFA') || columnName.includes('DIPA')) return 'degrees';
  
  // Temperature (specific formats first)
  if (columnName.includes('(celcius)') || columnName.includes('Temperature (celcius)')) return '°C';
  if (columnName.includes('Temperature') || columnName.includes('TEMP')) return 'degrees';
  
  // Magnetic measurements (including 3.4.6 format)
  if (columnName.includes('MagF') || columnName.includes('MAGF') || columnName.includes('MX') || columnName.includes('MY') || columnName.includes('MZ')) return 'gauss';
  if (columnName.includes('Magnetic Field') || columnName.includes('Mx (gauss)') || columnName.includes('My (gauss)') || columnName.includes('Mz (gauss)')) return 'gauss';
  if (columnName.includes('Magnetic Toolface')) return 'degrees';
  
  // Gravity and accelerometer measurements (including 3.4.6 format)
  if (columnName.includes('Total Grav') || columnName.includes('GRAV') || columnName.includes('GX') || columnName.includes('GY') || columnName.includes('GZ')) return 'g';
  if (columnName.includes('Gravity (g)') || columnName.includes('Ax (g)') || columnName.includes('Ay (g)') || columnName.includes('Az (g)')) return 'g';
  if (columnName.includes('Ax2 (g)') || columnName.includes('Ay2 (g)') || columnName.includes('Az2 (g)')) return 'g';
  
  // Time measurements
  if (columnName.includes('Clock Ticks')) return 'ms';
  
  // Vibration measurements
  if (columnName.includes('Vibe') && columnName.includes('g RMS')) return 'g RMS';
  if (columnName.includes('Max Shock') && columnName.includes('g')) return 'g';
  if (columnName.includes('Shock Counts')) return 'counts';
  if (columnName.includes('VIB') && columnName.includes('RMS')) return 'g RMS';
  if (columnName.includes('VIB') && columnName.includes('PEAK')) return 'g';
  
  // Performance measurements
  if (columnName.includes('PROPEFF')) return '%';
  if (columnName.includes('RXERR')) return '%';
  if (columnName.includes('SNR')) return 'dB';
  if (columnName.includes('CPS')) return 'CPS';
  
  // New 3.4.6 voltage measurements (statistical)
  if (columnName.includes('Battery') && columnName.includes('(V)')) return 'V';
  if (columnName.includes('Rail') && columnName.includes('(V)')) return 'V';
  if (columnName.includes('System Current') && columnName.includes('(V)')) return 'V';
  
  // New 3.4.6 gamma measurements
  if (columnName.includes('Gamma1 (API/CPS)') || columnName.includes('Gamma2 (API/CPS)') || columnName.includes('Gamma3 (API/CPS)')) return 'API/CPS';
  if (columnName.includes('Rotation Gamma Sector') && columnName.includes('(CPS/sector)')) return 'CPS/sector';
  if (columnName.includes('AziGamma Survey Toolface Offset')) return 'degrees';
  
  // New 3.4.6 quality and status measurements
  if (columnName.includes('G Error Factor')) return '';
  if (columnName.includes('Continuous Survey Status')) return '';
  if (columnName.includes('ADC Temperature (mV)')) return 'mV';
  
  // HopRx-specific measurements
  if (columnName.includes('(mV)') || columnName.includes('_min (mV)') || columnName.includes('_max (mV)') || columnName.includes('_mean (mV)')) return 'mV';
  if (columnName.includes('(mA)') || columnName.includes('_min (mA)') || columnName.includes('_max (mA)') || columnName.includes('_mean (mA)')) return 'mA';
  if (columnName.includes('avg_symbol_snr') || columnName.includes('min_symbol_snr')) return 'dB';
  if (columnName.includes('packet_count') || columnName.includes('bits_received') || columnName.includes('bit_errors') || columnName.includes('bits_corrected')) return 'count';
  if (columnName.includes('Event') || columnName.includes('Data')) return '';
  
  // HopRx standard deviation units (match based on parameter prefix)
  if (columnName.includes('_std_dev')) {
    if (columnName.includes('batt') || columnName.includes('bbus') || columnName.includes('v8_') || columnName.includes('v3_') || columnName.includes('v0_')) return 'mV';
    if (columnName.includes('cur_') || columnName.includes('current')) return 'mA';
    return 'units'; // Generic std dev
  }
  
  return 'units';
}